import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Upload, Loader2, AlertTriangle } from "lucide-react";

const GRUPPI = { soggetti: "Clienti / Sub-vettori", localita: "Località", prodotti: "Prodotti" };

function Stat({ label, value, tone = "" }) {
  return (
    <div className={`rounded-md p-3 border ${tone || "bg-slate-50 border-slate-200"}`}>
      <div className="text-xs font-semibold uppercase">{label}</div>
      <div className="text-2xl font-head font-bold" data-testid={`lst-import-count-${label.toLowerCase().replace(/\W+/g, "-")}`}>{value}</div>
    </div>
  );
}

export default function ImportListiniDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [create, setCreate] = useState({ soggetti: false, localita: false, prodotti: false });
  const [decisions, setDecisions] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (open) { setFile(null); setPreview(null); setResult(null); setDecisions({}); setCreate({ soggetti: false, localita: false, prodotti: false }); }
  }, [open]);

  const runPreview = async (f, cr) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("create", JSON.stringify(cr));
      const { data } = await api.post("/listini-excel/import/preview", fd);
      setPreview(data);
      setDecisions((d) => {
        const n = { ...d };
        data.listini.filter((L) => L.esistente).forEach((L) => { if (!n[L.nome]) n[L.nome] = { azione: "nuova_versione", valido_dal: L.valido_dal || "" }; });
        return n;
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (f) => { if (!f) return; setFile(f); runPreview(f, create); };
  const toggleCreate = (g) => { const cr = { ...create, [g]: !create[g] }; setCreate(cr); runPreview(file, cr); };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("create", JSON.stringify(create));
      fd.append("decisions", JSON.stringify(decisions));
      const { data } = await api.post("/listini-excel/import/commit", fd);
      setResult(data);
      onImported?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const c = preview?.counts;
  const groups = preview ? [...new Set(preview.missing.map((m) => m.gruppo))] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="import-listini-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Importa listini da Excel</DialogTitle>
          <DialogDescription>Foglio "Tariffe" (una riga per tariffa, stesso nome listino = stesso listino) e foglio "Supplementi". Nulla viene salvato prima della conferma.</DialogDescription>
        </DialogHeader>

        {!preview && !result && (
          <div className="py-6">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center cursor-pointer hover:border-sky-400 transition-colors" onClick={() => fileRef.current?.click()} data-testid="lst-import-dropzone">
              {busy ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-500" /> : <Upload className="h-8 w-8 mx-auto text-slate-400" />}
              <div className="mt-3 text-sm text-slate-600">Clicca per selezionare il file <strong>.xlsx</strong> (usa il modello scaricabile)</div>
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="lst-import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
          </div>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Stat label="Listini" value={c.listini} tone="badge-verde" />
              <Stat label="Tariffe" value={c.tariffe} tone="badge-verde" />
              <Stat label="Supplementi" value={c.supplementi} />
              <Stat label="Esistenti" value={c.esistenti} tone="bg-sky-50 border-sky-200 text-sky-800" />
              <Stat label="Duplicati" value={c.duplicati} tone="badge-giallo" />
              <Stat label="Errori" value={c.errori} tone="badge-rosso" />
            </div>

            {groups.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2" data-testid="lst-import-missing">
                <div className="text-sm font-medium text-amber-900 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Voci non trovate in anagrafica</div>
                {groups.map((g) => (
                  <div key={g} className="text-xs">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold">{GRUPPI[g]}:</span>
                      <span className="text-slate-700">{preview.missing.filter((m) => m.gruppo === g).map((m) => `${m.valore} (righe ${m.righe.join(", ")})`).join(" · ")}</span>
                    </div>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" checked={!!create[g]} onChange={() => !create[g] && toggleCreate(g)} data-testid={`lst-import-create-${g}`} />Crea in anagrafica</label>
                      <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" checked={!create[g]} onChange={() => create[g] && toggleCreate(g)} data-testid={`lst-import-skip-${g}`} />{g === "soggetti" ? "Non importare quei listini" : "Scarta le righe"}</label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-slate-200 rounded-md overflow-auto text-xs">
              <table className="w-full" data-testid="lst-import-listini">
                <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1.5">Listino</th><th className="text-left px-2 py-1.5">Soggetto</th><th className="text-right px-2 py-1.5">Tariffe</th><th className="text-right px-2 py-1.5">Suppl.</th><th className="text-left px-2 py-1.5">Validità</th><th className="text-left px-2 py-1.5">Stato / azione</th></tr></thead>
                <tbody>
                  {preview.listini.map((L) => (
                    <tr key={L.nome} className="border-t border-slate-100 align-top" data-testid={`lst-import-row-${L.nome}`}>
                      <td className="px-2 py-1.5 font-medium">{L.nome}</td>
                      <td className="px-2 py-1.5">{L.soggetto_nome} <span className="text-slate-400">({L.soggetto_tipo})</span>{L.soggetto_mancante && <span className="text-red-600 ml-1">non in anagrafica</span>}</td>
                      <td className="px-2 py-1.5 text-right">{L.tariffe.length}{L.scartate ? <span className="text-amber-700"> (−{L.scartate})</span> : null}</td>
                      <td className="px-2 py-1.5 text-right">{L.supplementi.length}</td>
                      <td className="px-2 py-1.5">{L.valido_dal ? formatDate(L.valido_dal) : "—"} → {L.valido_al ? formatDate(L.valido_al) : "∞"}</td>
                      <td className="px-2 py-1.5">
                        {L.soggetto_mancante ? <span className="text-red-600">Bloccato</span> : !L.esistente ? <span className="text-emerald-700">Nuovo</span> : (
                          <div className="space-y-1">
                            <div className="text-sky-800">Esiste già ({L.esistente.n_tariffe} tariffe, dal {L.esistente.valido_dal ? formatDate(L.esistente.valido_dal) : "—"})</div>
                            <div className="flex flex-wrap gap-2 items-center">
                              {[["nuova_versione", "Nuova versione"], ["aggiorna", "Aggiorna attuale"], ["salta", "Salta"]].map(([v, l]) => (
                                <label key={v} className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={decisions[L.nome]?.azione === v} onChange={() => setDecisions((d) => ({ ...d, [L.nome]: { ...d[L.nome], azione: v } }))} data-testid={`lst-import-dec-${L.nome}-${v}`} />{l}</label>
                              ))}
                              {decisions[L.nome]?.azione === "nuova_versione" && (
                                <span className="flex items-center gap-1">dal <Input type="date" className="h-6 text-xs w-36" value={decisions[L.nome]?.valido_dal || ""} onChange={(e) => setDecisions((d) => ({ ...d, [L.nome]: { ...d[L.nome], valido_dal: e.target.value } }))} data-testid={`lst-import-dal-${L.nome}`} /> <span className="text-slate-500">(la precedente si chiude il giorno prima)</span></span>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(preview.errors.length > 0 || preview.duplicates.length > 0 || preview.warnings.length > 0) && (
              <div className="border border-slate-200 rounded-md max-h-40 overflow-auto text-xs p-2 space-y-0.5" data-testid="lst-import-issues">
                {preview.errors.map((e, i) => <div key={`e${i}`} className="text-red-700">Errore {e.foglio} riga {e.row}: {e.errors.join("; ")}</div>)}
                {preview.duplicates.map((d, i) => <div key={`d${i}`} className="text-amber-700">Riga {d.row} ({d.listino}): {d.detail}</div>)}
                {preview.warnings.map((w, i) => <div key={`w${i}`} className="text-slate-600">{w}</div>)}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>Altro file</Button>
              <Button onClick={commit} disabled={busy || c.listini === 0} data-testid="lst-import-commit">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Conferma e salva {c.listini} listini</Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-3 py-2" data-testid="lst-import-result">
            <div className="grid grid-cols-4 gap-2">
              <Stat label="Creati" value={result.creati.length} tone="badge-verde" />
              <Stat label="Nuove versioni" value={result.nuove_versioni.length} tone="bg-sky-50 border-sky-200 text-sky-800" />
              <Stat label="Aggiornati" value={result.aggiornati.length} />
              <Stat label="Saltati" value={result.saltati.length} tone="badge-giallo" />
            </div>
            {result.anagrafiche_create > 0 && <div className="text-xs text-slate-600">{result.anagrafiche_create} voci create in anagrafica.</div>}
            {result.saltati.length > 0 && <div className="text-xs text-amber-700">Saltati: {result.saltati.join(", ")}</div>}
            <DialogFooter><Button onClick={() => onOpenChange(false)} data-testid="lst-import-close">Chiudi</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
